import * as Sentry from '@sentry/react';
import { makeStateStore } from '@playcastdotio/StateStore';
import type { PlaycastGamepadId, StateStore } from '@playcastdotio/CoreTypes';
import type { PeerMetadata } from '@playcastdotio/Messaging';
import { CoordinatorEventImpl } from '../_types/events';
import type { PeerState, PeerEvents, Player } from './stores';
import type { MediaCoordinator } from '../media/MediaCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type {
  ConnectedPeer,
  PeerType,
  PeerPermissions,
  IPeerConnection,
  IPeerConnectionFactory,
  ISignalingService,
  SerializableMessage,
  PeerIdAssignedData,
} from './types';
import { DEFAULT_PERMISSIONS } from './types';
import {
  ConnectionSupersededError,
  PeerConnectionManager,
} from './managers/PeerConnectionManager';
import type { QualityTierDemotionCause, QualityTierDemotionContext } from './managers/RecoveryCapacityGate';
import { ShadowcamCaptureManager } from '../media/managers/capture/ShadowcamCaptureManager';
import { PeerTypeRegistry } from './managers/PeerTypeRegistry';
import { ShadowcamTierSelector } from './managers/ShadowcamTierSelector';
import { profileForShadowcamName, trackMeetsRequestedShadowcam } from '../media/shadowcamCaptureProfiles';
import { sinkWebrtcDebugLine } from './managers/webrtcDebugSink';
import { SilentAudioWatchdog } from './managers/SilentAudioWatchdog';
import { PeerStatsManager, type PeerStats } from './managers/PeerStatsManager';
import { InputControlManager } from './managers/InputControlManager';
import type { InputConfigStores, GamepadConfigState } from './stores/inputConfigStores';
import type { GamepadManager } from './managers/GamepadManager';
import type { ActivityTracker } from './managers/ActivityTracker';
import { fetchIceServersFromApi } from '../../services/peer/iceServerFetch';
import { BASE_STREAM_CAMERA } from '../media/cameraNames';
import {
  isPlayerActivityMessage,
  isGuestDebugMessage,
  isBeaconE2eHooksEnabled,
  isDebugWebrtcLoggingEnabled,
  BEACON_WEBRTC_E2E_HOOKS_ENV,
  PLAYCAST_BEACON_E2E_WINDOW_KEY,
  normalizeHostEndReason,
  STREAM_SIGNAL_QUALITY_MESSAGE_TYPE,
  HOST_PLAYER_RECONNECT_HOLD_MS,
  QUALITY_TIER_SHADOWCAM,
  readInputPhotonSeq,
  tierForShadowcam,
  type QualityTier,
  type SharedTierInput,
  type HardwareCodecProbeResult,
  type PlaycastBeaconE2eApi,
  type SoftwareEncodeRecoveryState,
  type SessionHealthRecord,
  type GuestLinkDebugSnapshot,
} from '@playcastdotio/PeerCore';
import { StreamHealthManager } from './observability/StreamHealthManager';
import {
  detachBeaconInsertableImpairmentBridge,
  installBeaconInsertableImpairmentBridge,
} from './beaconInsertableImpairmentBridge';

const sentryLog = {
  info: (message: string): void => Sentry.logger?.info(message),
  warn: (message: string): void => Sentry.logger?.warn(message),
  error: (message: string): void => Sentry.logger?.error(message),
};

export type PeerCoordinatorConfig = {
  iceTransportPolicy?: RTCIceTransportPolicy;
  /**
   * Runs immediately before opening ShadowCam for an admin offer. Must refresh Playjector display→camera
   * binding and wait long enough for setSource (Electron uses ~500ms stagger) or the capture track can stay
   * on the ShadowCam placeholder forever (solid green in the admin dashboard).
   */
  beforeAdminMediaCapture?: () => Promise<void>;
  /**
   * Called reactively when a peer needs a shadowcam that hasn't been activated yet
   * (e.g. ShadowCam01 for mobile). Sends startStopToggle via Playjector IPC.
   * Resolves once Playjector acknowledges or after a reasonable timeout.
   * A rejected activation is non-fatal for legacy pavilion/admin capture: capture
   * still proceeds using the existing fallback behavior. Game instance sessions surface
   * their readiness state through SessionCastResult instead of aborting the offer.
   */
  activateShadowcam?: (shadowcamName: string) => Promise<void>;
  /**
   * Android multi-instance host Phase 5: late-bound resolver for the active
   * android launch's streamId (`GameInstanceLaunchManager.getActiveAndroidStreamId`,
   * wired in init.ts). While it resolves, instance session cameras are activated
   * BY capture (streams model) — `activateShadowcam` is skipped for session
   * peers, so the host never sends explicit bind/activate messages on the
   * android path. Legacy/mumu sessions keep the explicit activation.
   */
  getActiveAndroidStreamId?: () => string | undefined;
  /**
   * Called when the last peer needing an on-demand shadowcam leaves. Tears down
   * the Playjector startStopToggle so the camera does not stay sourced forever.
   */
  deactivateShadowcam?: (shadowcamName: string) => Promise<void>;
  /**
   * Re-assert AudioCaster as the Windows default render device before game-audio
   * capture (and on silent-attached recovery). Guest teardown may have restored
   * host speakers, leaving AudioCaster capture encoding silence.
   */
  ensureAudioCasterDefault?: () => Promise<void>;
};

/**
 * Resolves a game instance session for an incoming player offer. The
 * implementation bridges PeerCoordinator's instance-peer map to the
 * GameInstanceLaunchManager's session records.
 */
export type GameInstanceSessionResolver = {
  getSessionForInstance(gameInstanceIndex: number): NonNullable<PeerMetadata['playjectorSession']> | null;
};

/**
 * Occupancy seam for game instance sessions (pool-match-lifecycle Phase 2,
 * item 3). Wired to the MatchLifecycleManager in production. Both methods
 * return the session's player count after the change so PeerCoordinator can
 * apply capture-ownership rules: session capture stops only when the count
 * reaches 0, not on any single player disconnect.
 */
export type GameInstanceOccupancyTracker = {
  playerJoinedSession(sessionId: string): number;
  playerLeftSession(sessionId: string): number;
};

/**
 * PeerCoordinator - Orchestrates all WebRTC peer connections.
 *
 * Replaces the legacy host.ts module. Manages connections for all peer types
 * (player, admin, remoteHost) using injectable factories for testability.
 *
 * Uses dedicated events per peer type for best DX.
 * Subscribes to MediaCoordinator.tracksChanged to push track updates.
 */
const DIAG = '[AUDIO-BUG-12009:PeerCoordinator]';
const DEFAULT_PREWARM_INTERVAL_MS = 300_000; // 5 minutes
// Android multi-instance Phase 5 (coordinated rename): the desktop/base camera
// comes from the single shared constant, mirrored to C++ Constants::BASE_STREAM_CAMERA.
const DESKTOP_SHADOWCAM = BASE_STREAM_CAMERA;
/** Admin dashboard uses the same desktop ShadowCam as the player stream (the base stream camera). */
const ADMIN_SHADOWCAM = DESKTOP_SHADOWCAM;

export class PeerCoordinator {
  private peerStore: StateStore<PeerState>;
  private connectionManager: PeerConnectionManager;
  /** Always-on stream-health observability bus (Phase 3 backbone), owned here. */
  private readonly streamHealth = new StreamHealthManager();
  private typeRegistry: PeerTypeRegistry;
  private tierSelector = new ShadowcamTierSelector();
  private statsManager: PeerStatsManager;
  private inputControl: InputControlManager;
  private mediaCoordinator: MediaCoordinator | null = null;
  private signalingService: ISignalingService | null = null;
  private iceTransportPolicy: RTCIceTransportPolicy = 'all';
  private beforeAdminMediaCapture: (() => Promise<void>) | null = null;
  private activateShadowcam: ((shadowcamName: string) => Promise<void>) | null = null;
  private getActiveAndroidStreamId: (() => string | undefined) | null = null;
  private deactivateShadowcam: ((shadowcamName: string) => Promise<void>) | null = null;
  private ensureAudioCasterDefault: (() => Promise<void>) | null = null;
  private silentAudioWatchdog: SilentAudioWatchdog | null = null;
  private activatedShadowcams = new Set<string>([DESKTOP_SHADOWCAM]);
  /**
   * Shadowcams owned by a game instance session (session.shadowCamName). They are managed by
   * the session's capture lifecycle, so they are excluded from on-demand teardown and from the
   * shared-capture tier tally.
   */
  private sessionShadowcams = new Set<string>();
  /** Cameras queued for deactivation but not yet torn down; activation cancels the pending teardown. */
  private pendingDeactivations = new Set<string>();
  /**
   * Peers a recovery / decode-lag demotion stepped down, and the tier it stepped them down TO.
   *
   * A set of ids was enough while only mobile could be demoted, because "demoted" then always
   * meant the floor. It stopped being enough once a desktop guest can be demoted to 480p: the
   * shared-capture tally represented every demoted peer as a questionable mobile one, so a
   * demoted desktop peer would have dragged the whole capture to 360p on its behalf.
   *
   * Cleared when the stream goes idle (player count → 0), on successful non-recovery re-seat,
   * or when a reconnect hold expires without reclaim. Not cleared mid-session on leave alone
   * while other players remain — shared capture is never promoted mid-session.
   */
  private recoveryDemotedTierByPeerId = new Map<string, QualityTier>();
  /**
   * Permissions preserved across a disconnect→reclaim window so a returning guest keeps the
   * host's edits instead of snapping back to DEFAULT_PERMISSIONS.
   */
  private preservedPermissionsByPeerId = new Map<string, PeerPermissions>();
  /** Coalesces idle baseline reset so a reclaim teardown does not race the zero-player path. */
  private pendingSharedPromoteTimer: ReturnType<typeof setTimeout> | null = null;
  /** Generation token to abandon in-flight idle baseline resets when a demotion intervenes. */
  private sharedPromoteGeneration = 0;
  /**
   * Serializes every shadowcam capture→replaceTrack chain. Two chains running concurrently
   * would each install-and-stop the other's capture: the second capture stops the track the
   * first just moved every sender onto, and its replaceTrack(oldRef, new) matches no sender
   * (replaceTrack matches by exact reference), stranding all peers on a stopped track — a
   * silent, console-only blackout for the whole session on the mobile-flap path.
   */
  private shadowcamSwapChain: Promise<unknown> = Promise.resolve();
  private shadowcamActivations = new Map<string, Promise<void>>();
  private unsubscribers: Array<() => void> = [];
  private preWarmRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private playerCounter = 0;
  private captureRequestSequence = 0;
  private captureLifecycle = 0;
  private peerNameMap = new Map<string, string>();
  private previousVideoTrack: MediaStreamTrack | null = null;
  private previousAudioTrack: MediaStreamTrack | null = null;
  private sessionResolver: GameInstanceSessionResolver | null = null;
  private occupancyTracker: GameInstanceOccupancyTracker | null = null;
  /** Maps instance peerId → gameInstanceIndex for N-peer routing. */
  private instancePeerIds = new Map<string, number>();
  /** Resolves an instance's HOST peer id from its index (GameInstancePeerManager in production). */
  private gameInstancePeerIdResolver: ((gameInstanceIndex: number) => string | undefined) | null = null;
  /**
   * player peerId → the local HOST peer id its signaling is sent FROM. v2
   * session connections answer from the instance peer the player offered to —
   * a v2 host never holds the v1 global host peer (its auth-time request only
   * fires for an already-streaming host, and the v1 startStream request is
   * gated off), so keying outbound signaling off the global id silently
   * dropped every answer.
   */
  private readonly connectionFromIds = new Map<string, string>();
  /** Defers playerCountChanged(0) while guests may reclaim within the hold window. */
  private deferredZeroPlayerCountTimer: ReturnType<typeof setTimeout> | null = null;

  public events: PeerEvents = {
    onPlayerJoined: new CoordinatorEventImpl(),
    onPlayerLeft: new CoordinatorEventImpl(),
    onAdminJoined: new CoordinatorEventImpl(),
    onAdminLeft: new CoordinatorEventImpl(),
    onRemoteHostJoined: new CoordinatorEventImpl(),
    onRemoteHostLeft: new CoordinatorEventImpl(),
    playerCountChanged: new CoordinatorEventImpl(),
    hostPeerIdChanged: new CoordinatorEventImpl(),
    remoteHostPeerIdChanged: new CoordinatorEventImpl(),
    allPeersDisconnected: new CoordinatorEventImpl(),
    permissionsChanged: new CoordinatorEventImpl(),
    dataReceived: new CoordinatorEventImpl(),
    pauseStateChanged: new CoordinatorEventImpl(),
    gamepadSlotsChanged: new CoordinatorEventImpl(),
    afkStateChanged: new CoordinatorEventImpl(),
  };

  /** Host beacon UI subscribes — software encode warning after accepted-software. */
  public readonly hardwareCodecStatus = new CoordinatorEventImpl<{
    peerId: string;
    result: HardwareCodecProbeResult;
    softwareEncodeRecoveryState?: SoftwareEncodeRecoveryState;
  }>();

  constructor(connectionFactory: IPeerConnectionFactory) {
    this.peerStore = makeStateStore<PeerState>({
      peers: {},
      players: {},
      playerCount: 0,
      hostPeerId: undefined,
      remoteHostPeerId: undefined,
      streamStatus: 'idle',
    });

    this.connectionManager = new PeerConnectionManager(connectionFactory, {
      onPeerConnected: (peerId, peerType, metadata) => this.handlePeerConnected(peerId, peerType, metadata),
      onPeerDisconnected: (peerId, peerType) => this.handlePeerDisconnected(peerId, peerType),
      onSignalGenerated: (peerId, description) => this.handleSignalGenerated(peerId, description),
      onIceCandidatesGenerated: (peerId, candidates) => this.handleIceCandidatesGenerated(peerId, candidates),
      onRemoteStream: (peerId, stream) => this.handleRemoteStream(peerId, stream),
      onDataChannelMessage: (peerId, data) => this.handleDataChannelMessage(peerId, data),
      onHardwareCodecStatus: (peerId, result, softwareEncodeRecoveryState) => {
        this.hardwareCodecStatus.emit({
          peerId,
          result,
          softwareEncodeRecoveryState,
        });
      },
      // Report delivery back: the manager only latches a level it knows arrived, so a send
      // into a channel that is not open yet is retried instead of being silently lost.
      onSignalQuality: (peerId, level) => {
        try {
          return this.connectionManager.sendData(
            peerId,
            JSON.stringify({ type: STREAM_SIGNAL_QUALITY_MESSAGE_TYPE, level }),
          );
        } catch {
          // Channel may be closed during teardown
          return false;
        }
      },
      onSoftwareEncodeRefused: (peerId, encoderImplementation) =>
        this.handleSoftwareEncodeRefused(peerId, encoderImplementation),
      onReconnectHoldExpired: () => this.handleReconnectHoldExpired(),
      onReconnectHoldArmed: (peerId) => this.handleReconnectHoldArmed(peerId),
      onPlayerTransportTrouble: (peerId, metadata) =>
        this.handlePlayerTransportTrouble(peerId, metadata),
      onDecodeLagTierDemotion: (peerId, tier, cause, context) =>
        this.handleDecodeLagTierDemotion(peerId, tier, cause, context),
      // The live shared capture, so congestion cuts are sized against the picture the
      // encoders are actually being fed rather than each peer's own tier hint.
      getCaptureResolution: () => {
        const settings = this.mediaCoordinator?.getVideoTrack()?.getSettings();
        if (!settings?.width || !settings.height) return null;
        return { width: settings.width, height: settings.height };
      },
      getCaptureFrameRate: () => {
        const fps = this.mediaCoordinator?.getVideoTrack()?.getSettings().frameRate;
        return typeof fps === 'number' && Number.isFinite(fps) && fps > 0 ? fps : null;
      },
      onPeerConnectionRegistered: (peerId, peerType) =>
        this.handlePeerConnectionRegistered(peerId, peerType),
      onGameAudioHealthSample: (peerId, sample) => {
        this.silentAudioWatchdog?.observe(peerId, sample);
      },
    });
    // Wire the always-on stream-health bus into the connection manager: guest streamHealth
    // messages and host decision events flow into the per-session timeline from here on.
    this.connectionManager.setStreamHealth(this.streamHealth);

    this.typeRegistry = new PeerTypeRegistry();
    this.statsManager = new PeerStatsManager();
    this.inputControl = new InputControlManager(this.statsManager, {
      pauseStateChanged: this.events.pauseStateChanged,
      gamepadSlotsChanged: this.events.gamepadSlotsChanged,
      afkStateChanged: this.events.afkStateChanged,
    });

    this.registerDefaultHandlers();
    this.attachBeaconE2eHooksIfEnabled();
  }

  private readBeaconE2eHooksEnv(): string | undefined {
    if (typeof process !== 'undefined' && process.env) {
      return process.env[BEACON_WEBRTC_E2E_HOOKS_ENV];
    }
    return undefined;
  }

  /** The always-on stream-health observability bus (Phase 3 backbone), for a HUD/overlay. */
  public getStreamHealth(): StreamHealthManager {
    return this.streamHealth;
  }

  /** Current per-session stream-health record for a peer, if any (convenience for the HUD). */
  public getStreamHealthRecord(peerId: string): SessionHealthRecord | undefined {
    return this.streamHealth.recordFor(peerId);
  }

  private attachBeaconE2eHooksIfEnabled(): void {
    if (typeof window === 'undefined') return;
    if (!isBeaconE2eHooksEnabled(this.readBeaconE2eHooksEnv())) return;
    // Patch RTCPeerConnection before any peer connections are created.
    installBeaconInsertableImpairmentBridge();
    const api: PlaycastBeaconE2eApi = {
      getSnapshot: () => this.connectionManager.getBeaconE2eSnapshot(),
    };
    (window as unknown as Record<string, unknown>)[PLAYCAST_BEACON_E2E_WINDOW_KEY] = api;
  }

  private detachBeaconE2eHooks(): void {
    if (typeof window === 'undefined') return;
    detachBeaconInsertableImpairmentBridge();
    delete (window as unknown as Record<string, unknown>)[PLAYCAST_BEACON_E2E_WINDOW_KEY];
  }

  public async init(
    sessionCoordinator?: SessionCoordinator,
    mediaCoordinator?: MediaCoordinator,
    signalingService?: ISignalingService,
    config?: PeerCoordinatorConfig,
  ): Promise<void> {
    console.log('[PeerCoordinator] Initializing...');

    this.mediaCoordinator = mediaCoordinator ?? null;
    this.signalingService = signalingService ?? null;
    this.iceTransportPolicy = config?.iceTransportPolicy ?? 'all';
    this.beforeAdminMediaCapture = config?.beforeAdminMediaCapture ?? null;
    this.activateShadowcam = config?.activateShadowcam ?? null;
    this.getActiveAndroidStreamId = config?.getActiveAndroidStreamId ?? null;
    this.deactivateShadowcam = config?.deactivateShadowcam ?? null;
    this.ensureAudioCasterDefault = config?.ensureAudioCasterDefault ?? null;
    this.silentAudioWatchdog = mediaCoordinator
      ? new SilentAudioWatchdog({
          recaptureGameAudio: () => mediaCoordinator.startGameAudioCapture(),
          ensureAudioCasterDefault: () =>
            this.ensureAudioCasterDefault?.() ?? Promise.resolve(),
        })
      : null;

    if (sessionCoordinator?.events?.streamStarted) {
      const unsub = sessionCoordinator.events.streamStarted.addListener(() => {
        console.log('[PeerCoordinator] Stream started, ensuring media capture');
        // Same lock as an arriving offer. An unlocked desktop warm-up used to finish
        // after the iPhone's sender existed and replace its capture with 720p.
        void this.runSerializedShadowcamSwap(() => this.ensureMediaCapture()).catch((error) => {
          console.warn('[PeerCoordinator] Failed to ensure media capture during stream-start warm-up:', error);
        });
      });
      this.unsubscribers.push(unsub);
    }

    if (sessionCoordinator?.events?.warmthArmed) {
      const unsub = sessionCoordinator.events.warmthArmed.addListener(() => {
        console.log('[PeerCoordinator] Host joinable, ensuring media capture before the next player');
        void this.runSerializedShadowcamSwap(() => this.ensureMediaCapture()).catch((error) => {
          console.warn('[PeerCoordinator] Failed to ensure media capture during warmth-armed warm-up:', error);
        });
      });
      this.unsubscribers.push(unsub);
    }

    if (sessionCoordinator?.events?.captureReleaseRequested) {
      const unsub = sessionCoordinator.events.captureReleaseRequested.addListener(() => {
        console.log('[PeerCoordinator] Host left the joinable pool, stopping retained capture');
        this.mediaCoordinator?.stopAllCapture();
      });
      this.unsubscribers.push(unsub);
    }

    if (sessionCoordinator) {
      this.connectionManager.setPipelineSettledAtProvider(() => sessionCoordinator.getPipelineSettledAtMs());
    }

    if (sessionCoordinator?.events?.streamStopped) {
      const unsub = sessionCoordinator.events.streamStopped.addListener((event) => {
        console.log('[PeerCoordinator] Stream stopped, disconnecting all peers', {
          retainCapture: event.retainCapture,
        });
        this.updateHostPeerId(undefined);
        this.clearAllPlayers();
        this.disconnectAllPeers('host:stream_stopped');
        if (!event.retainCapture) {
          this.mediaCoordinator?.stopAllCapture();
        }
      });
      this.unsubscribers.push(unsub);
    }

    if (mediaCoordinator) {
      const unsub = mediaCoordinator.events.tracksChanged.addListener(({ videoTrack, audioTrack }) => {
        this.handleTracksChanged(videoTrack, audioTrack);
      });
      this.unsubscribers.push(unsub);
    }

    if (this.signalingService) {
      const unsub = this.signalingService.onSignalingMessage((msg) => {
        this.handleSignalingMessage(msg).catch((error) => {
          this.reportSignalingFailure(msg.type, msg.fromId, error);
        });
      });
      this.unsubscribers.push(unsub);

      const unsubPeerId = this.signalingService.onPeerIdAssigned((data) => {
        this.handlePeerIdAssigned(data);
      });
      this.unsubscribers.push(unsubPeerId);
    }

    console.log('[PeerCoordinator] Initialized');
  }

  public destroy(): void {
    this.captureLifecycle++;
    console.log('[PeerCoordinator] Destroying...');
    this.stopPreWarmCycle();
    this.cancelDeferredZeroPlayerCount();
    this.cancelPendingSharedPromote();
    this.preservedPermissionsByPeerId.clear();
    this.detachBeaconE2eHooks();
    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];
    this.connectionFromIds.clear();
    this.sessionShadowcams.clear();
    this.connectionManager.destroyAll();
    this.statsManager.destroy();
    this.typeRegistry.destroy();
    console.log('[PeerCoordinator] Destroyed');
  }

  // ========================================
  // Public API - Pre-Warm Lifecycle
  // ========================================

  /**
   * Begin a periodic cycle: fetch ICE credentials, pre-warm an RTCPeerConnection,
   * and refresh every `intervalMs` to keep TURN credentials fresh.
   * The pre-warmed connection is purely local -- no server-side state changes.
   */
  public async startPreWarmCycle(intervalMs = DEFAULT_PREWARM_INTERVAL_MS): Promise<void> {
    this.stopPreWarmCycle();
    await this.refreshPreWarm();

    this.preWarmRefreshTimer = setInterval(async () => {
      await this.refreshPreWarm();
    }, intervalMs);

    console.log(`[PeerCoordinator] Pre-warm cycle started (interval=${intervalMs}ms)`);
  }

  public stopPreWarmCycle(): void {
    if (this.preWarmRefreshTimer) {
      clearInterval(this.preWarmRefreshTimer);
      this.preWarmRefreshTimer = null;
    }
    this.connectionManager.destroyPreWarmedConnection();
    console.log('[PeerCoordinator] Pre-warm cycle stopped');
  }

  /**
   * Initial ShadowCam from guest metadata (decision 1B / 4C).
   * Mid-session congestion must not call this for Cam swaps — recovery is fps+bitrate only.
   */
  public selectShadowcam(metadata?: PeerMetadata): string {
    if (metadata?.playjectorSession?.shadowCamName) {
      return metadata.playjectorSession.shadowCamName;
    }
    return this.tierSelector.selectInitialShadowcam(metadata);
  }

  /**
   * Offer metadata the capture may act on. A phone-sized Apple screen is mobile
   * even when the payload says otherwise, so it cannot inherit ShadowCam03.
   */
  private captureMetadata(metadata: PeerMetadata): PeerMetadata {
    if (this.tierSelector.effectiveIsMobile(metadata) !== true || metadata.isMobile === true) {
      return metadata;
    }
    return { ...metadata, isMobile: true };
  }

  private async refreshPreWarm(): Promise<void> {
    const creds = await fetchIceServersFromApi();
    if (!creds) {
      console.warn('[PeerCoordinator] Pre-warm skipped: ICE server fetch returned null');
      return;
    }
    this.connectionManager.setIceConfig({
      iceServers: [creds],
      iceTransportPolicy: this.iceTransportPolicy,
    });
    this.connectionManager.preWarm();
    console.log('[PeerCoordinator] Pre-warm refreshed');
  }

  /**
   * Trigger an immediate re-warm after the current pre-warmed connection is consumed.
   * Does not wait for the next interval tick.
   */
  private triggerImmediateReWarm(): void {
    this.refreshPreWarm().catch((err) =>
      console.warn('[PeerCoordinator] Immediate re-warm failed:', err),
    );
  }

  // ========================================
  // Public API - Getters
  // ========================================

  public getPlayerCount(): number {
    return this.peerStore.currentState.playerCount;
  }

  public getPlayers(): Player[] {
    return Object.values(this.peerStore.currentState.players);
  }

  public getPlayer(playerId: string): Player | undefined {
    return this.peerStore.currentState.players[playerId];
  }

  public getConnectedPeers(): ConnectedPeer[] {
    return Object.values(this.peerStore.currentState.peers);
  }

  public getConnectedPeer(peerId: string): ConnectedPeer | undefined {
    return this.peerStore.currentState.peers[peerId];
  }

  public getPeerPlayjectorSession(
    peerId: string,
  ): NonNullable<PeerMetadata['playjectorSession']> | undefined {
    return this.getConnectedPeer(peerId)?.metadata.playjectorSession;
  }

  public setGameInstanceSessionResolver(resolver: GameInstanceSessionResolver | null): void {
    this.sessionResolver = resolver;
  }

  /**
   * Wire per-session occupancy tracking (MatchLifecycleManager in production).
   * When unset, session capture stops on any player disconnect (legacy
   * single-player-per-session behavior).
   */
  public setGameInstanceOccupancyTracker(tracker: GameInstanceOccupancyTracker | null): void {
    this.occupancyTracker = tracker;
  }

  /**
   * Wire the instance-index → HOST peer id resolver (GameInstancePeerManager
   * in production). Session-bound connections signal FROM their instance peer,
   * never from the v1 global host peer.
   */
  public setGameInstancePeerIdResolver(
    resolver: ((gameInstanceIndex: number) => string | undefined) | null,
  ): void {
    this.gameInstancePeerIdResolver = resolver;
  }

  /**
   * Register an instance-specific HOST peer. Call when an instance transitions
   * to `ready` and the API assigns it a peerId via `PEER_GET_ID`.
   */
  public registerInstancePeer(peerId: string, gameInstanceIndex: number): void {
    this.instancePeerIds.set(peerId, gameInstanceIndex);
  }

  /**
   * Deregister an instance-specific HOST peer. Call when an instance leaves
   * the `ready` state or is torn down.
   */
  public deregisterInstancePeer(peerId: string): void {
    this.instancePeerIds.delete(peerId);
  }

  /** Get the game instance index for a registered instance peer. */
  public getInstanceIndexForPeer(peerId: string): number | undefined {
    return this.instancePeerIds.get(peerId);
  }

  /** Get all registered instance peer IDs. */
  public getInstancePeerIds(): ReadonlyMap<string, number> {
    return this.instancePeerIds;
  }

  public getPeersByType(peerType: PeerType): ConnectedPeer[] {
    return this.getConnectedPeers().filter((p) => p.peerType === peerType);
  }

  public getHostPeerId(): string | undefined {
    return this.peerStore.currentState.hostPeerId;
  }

  public getRemoteHostPeerId(): string | undefined {
    return this.peerStore.currentState.remoteHostPeerId;
  }

  public setBitrateOverride(bitrateBps: number | null): void {
    this.connectionManager.setBitrateOverride(bitrateBps);
  }

  public getBitrateOverride(): number | null {
    return this.connectionManager.getBitrateOverride();
  }

  // ========================================
  // Public API - Peer Lifecycle
  // ========================================

  /**
   * Accept an incoming SDP offer from a remote peer.
   * Reads peerMetadata to select tracks and permissions before answering.
   * @returns true if signal succeeded and the peer connection is live, false if signal failed.
   */
  public async acceptIncomingPeer(
    peerId: string,
    peerType: PeerType,
    metadata: PeerMetadata,
    sdp: RTCSessionDescriptionInit,
    videoTrack: MediaStreamTrack | null,
    audioTrack: MediaStreamTrack | null,
  ): Promise<boolean> {
    if (this.connectionManager.isIceRestartOffer(peerId, sdp)) {
      // A same-session offer repairs the existing transport. Rebuilding the host PC
      // here would strand the guest's in-place ICE restart and reset its quality state.
      await this.connectionManager.handleRemoteSdp(peerId, sdp);
      await this.connectionManager.drainPendingIceCandidates(peerId);
      return true;
    }
    this.logAcceptIncomingPeerBreadcrumb(peerId, peerType, videoTrack, audioTrack);
    // v2: a player who offered to an instance peer is answered FROM that
    // instance peer. The 'signal' events fire during connection.signal(sdp)
    // below — before the peer lands in the store — so the fromId is resolved
    // here, at accept time, from the offer metadata.
    const instanceIndex = metadata.playjectorSession?.gameInstanceIndex;
    const instancePeerId = instanceIndex !== undefined
      ? this.gameInstancePeerIdResolver?.(instanceIndex)
      : undefined;
    if (instancePeerId) this.connectionFromIds.set(peerId, instancePeerId);
    const hadPreWarmBefore = this.connectionManager.hasPreWarmedConnection();

    let connection: IPeerConnection;
    try {
      // Await preferHardware (and burst start) before answering the remote offer.
      connection = await this.connectionManager.createHostSideConnection(
        peerId, peerType, metadata, videoTrack, audioTrack,
      );
    } catch (err) {
      if (err instanceof ConnectionSupersededError) {
        console.warn(`[PeerCoordinator] Abandoning superseded offer for ${peerId}`);
        if (hadPreWarmBefore && !this.connectionManager.hasPreWarmedConnection()) {
          this.triggerImmediateReWarm();
        }
        return false;
      }
      throw err;
    }

    // The connection can also be replaced or torn down between create and signal.
    // Applying this offer's SDP to a connection that is no longer the registered one
    // would drive a destroyed peer connection and strand the live one unanswered.
    if (this.connectionManager.getConnection(peerId) !== connection) {
      console.warn(`[PeerCoordinator] Connection for ${peerId} changed before signal — abandoning offer`);
      if (hadPreWarmBefore && !this.connectionManager.hasPreWarmedConnection()) {
        this.triggerImmediateReWarm();
      }
      return false;
    }

    try {
      await connection.signal(sdp);
    } catch (signalErr) {
      // signal() failed after registration. Check if this connection is still the
      // registered one — a concurrent re-offer may have replaced it during the await.
      // If so, the replacement owns this peer now and we should not tear it down.
      if (this.connectionManager.getConnection(peerId) !== connection) {
        console.warn(`[PeerCoordinator] signal() failed for ${peerId} but connection was replaced — abandoning`);
        throw signalErr;
      }
      // The connection was registered (which cancelled any deferred playerCountChanged(0)
      // and cleared reconnect hold) but never became fully ready. Clean up and re-arm
      // the reconnect hold so the guest can still reclaim within HOST_PLAYER_RECONNECT_HOLD_MS.
      // This is NOT a host-initiated teardown — it's a failed attempt that should keep the
      // reclaim window open.
      console.error(`[PeerCoordinator] signal() failed for ${peerId}:`, signalErr);
      Sentry.captureException(signalErr, {
        tags: { peer_stage: 'signal_after_register' },
        contexts: { peer: { peerId, peerType } },
      });
      // destroyConnectionForFailedSignal avoids host-initiated-destroy semantics so
      // the disconnect path can arm a reconnect hold for this guest. That destroy emits
      // 'disconnected' synchronously, which already re-arms the deferred playerCount=0 —
      // recomputing the count here would only duplicate the emit.
      this.connectionManager.destroyConnectionForFailedSignal(peerId);
      // Already captured above — do not rethrow into handleSignalingMessage's
      // reportSignalingFailure (that would double-report to Sentry).
      return false;
    }
    await this.connectionManager.drainPendingIceCandidates(peerId);
    this.connectionManager.recordOfferSessionId(peerId, sdp);

    if (hadPreWarmBefore && !this.connectionManager.hasPreWarmedConnection()) {
      this.triggerImmediateReWarm();
    }
    return true;
  }

  public async kickPeer(peerId: string, reason = 'host:kick'): Promise<void> {
    console.log(`[PeerCoordinator] Kicking peer: ${peerId} (${reason})`);
    this.sendCloseReason(peerId, reason);
    await this.delayThenDestroy(peerId);
    this.removePeerFromStore(peerId);
  }

  public disconnectAllPeers(reason = 'host:close'): void {
    const peerIds = Object.keys(this.peerStore.currentState.peers);
    for (const peerId of peerIds) {
      this.sendCloseReason(peerId, reason);
    }
    setTimeout(() => {
      this.connectionManager.destroyAll();
      for (const peerId of peerIds) {
        this.removePeerFromStore(peerId);
      }
    }, 100);
  }

  /**
   * End a session whose host cannot reach a hardware encoder.
   *
   * Uses a `host:`-prefixed reason so the guest treats it as a deliberate end and does not
   * retry into the same machine — reconnecting would only reproduce the same software
   * encode. The reason is sent on the data channel before teardown so the player sees why.
   */
  private handleSoftwareEncodeRefused(peerId: string, encoderImplementation?: string): void {
    console.error('[PeerCoordinator] Refusing session — hardware encode unavailable', {
      peerId,
      encoderImplementation,
    });
    this.sendCloseReason(peerId, 'host:software_encode_unavailable');
    void this.delayThenDestroy(peerId).then(() => {
      this.removePeerFromStore(peerId);
    });
  }

  private sendCloseReason(peerId: string, reason: string): void {
    try {
      const normalized = normalizeHostEndReason(reason);
      const msg = JSON.stringify({ __peerClose: true, reason: normalized });
      this.connectionManager.sendData(peerId, msg);
    } catch {
      // Connection may already be gone
    }
  }

  private delayThenDestroy(peerId: string): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.connectionManager.destroyConnection(peerId);
        resolve();
      }, 100);
    });
  }

  // ========================================
  // Public API - Permissions
  // ========================================

  public getPeerPermissions(peerId: string): PeerPermissions | undefined {
    return this.peerStore.currentState.peers[peerId]?.permissions;
  }

  /**
   * Record that an input was forwarded for injection, so the next encoded frame can be
   * tagged with it. Backs the guest's input-to-photon measurement.
   *
   * Takes the whole message rather than a sequence number so the wire detail — which
   * metadata key carries the sample — stays with the coordinator that owns the photon
   * wiring, instead of spreading into the input router.
   */
  public noteInputForwarded(peerId: string, message: { body?: { timings?: { metadata?: Record<string, string> | undefined }[] | undefined } | undefined }): void {
    const inputSeq = readInputPhotonSeq(message);
    if (inputSeq === null) return;
    this.connectionManager.noteInputForwardedForPhoton(peerId, inputSeq);
  }

  public updatePeerPermissions(peerId: string, permissions: Partial<PeerPermissions>): void {
    const peer = this.peerStore.currentState.peers[peerId];
    if (!peer) return;

    const updated = { ...peer.permissions, ...permissions };
    const updatedPeer = { ...peer, permissions: updated };
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      peers: { ...this.peerStore.currentState.peers, [peerId]: updatedPeer },
    });
    this.events.permissionsChanged.emit({ peerId, permissions: updated });
  }

  public updateAllPlayerPermissions(permissions: Partial<PeerPermissions>): void {
    for (const peer of this.getPeersByType('player')) {
      this.updatePeerPermissions(peer.peerId, permissions);
    }
  }

  // ========================================
  // Public API - Input Control (delegates to InputControlManager)
  // ========================================

  public registerInputStores(stores: InputConfigStores): void { this.inputControl.registerInputStores(stores); }
  public registerGamepadManager(manager: GamepadManager): void { this.inputControl.registerGamepadManager(manager); }
  public registerActivityTracker(tracker: ActivityTracker): void { this.inputControl.registerActivityTracker(tracker); }

  public setPeerPaused(peerId: string, paused: boolean): void {
    if (!this.getPlayer(peerId)) return;
    this.inputControl.setPeerPaused(peerId, paused);
  }
  public setGlobalPaused(paused: boolean): void { this.inputControl.setGlobalPaused(paused); }
  public isPeerPaused(peerId: string): boolean { return this.inputControl.isPeerPaused(peerId); }
  public isGloballyPaused(): boolean { return this.inputControl.isGloballyPaused(); }

  public assignGamepadSlot(peerId: string, slotIndex: number): void { this.inputControl.assignGamepadSlot(peerId, slotIndex); }
  public removeGamepadSlot(peerId: string, slotIndex: number): void { this.inputControl.removeGamepadSlot(peerId, slotIndex); }
  public getGamepadSlots(peerId: string): PlaycastGamepadId[] { return this.inputControl.getGamepadSlots(peerId); }
  public getGamepadConfig(): GamepadConfigState { return this.inputControl.getGamepadConfig(); }
  public updateGamepadConfig(config: Partial<GamepadConfigState>): void { this.inputControl.updateGamepadConfig(config); }
  public setGamepadAccess(peerId: string, enabled: boolean): void { this.updatePeerPermissions(peerId, { gamepad: enabled }); }

  public getPeerStats(peerId: string): PeerStats | undefined { return this.inputControl.getPeerStats(peerId); }
  public getAllPeerStats(): PeerStats[] { return this.inputControl.getAllPeerStats(); }
  /** The guest end of the link (codec, jitter buffer, glass-to-glass) for the Beacon debug overlay. */
  public getGuestLinkDebugSnapshot(peerId?: string): GuestLinkDebugSnapshot | undefined {
    return this.connectionManager.getGuestLinkDebugSnapshot(peerId);
  }

  public isPlayerAfk(peerId: string): boolean { return this.inputControl.isPlayerAfk(peerId); }

  // ========================================
  // Public API - Peer ID Management (legacy bridge)
  // ========================================

  public updateHostPeerId(peerId: string | undefined): void {
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      hostPeerId: peerId,
    });
    this.events.hostPeerIdChanged.emit(peerId);
  }

  public updateRemoteHostPeerId(peerId: string | undefined): void {
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      remoteHostPeerId: peerId,
    });
    this.events.remoteHostPeerIdChanged.emit(peerId);
  }

  // ========================================
  // Public API - Data Channel
  // ========================================

  public sendToPeer(peerId: string, data: string | ArrayBuffer): boolean {
    return this.connectionManager.sendData(peerId, data);
  }

  public broadcastToAll(data: string | ArrayBuffer): void {
    this.connectionManager.broadcastData(data);
  }

  // ========================================
  // Public API - Registry access (for extending)
  // ========================================

  public getTypeRegistry(): PeerTypeRegistry {
    return this.typeRegistry;
  }

  // ========================================
  // Legacy Bridge - called by legacy code during migration
  // ========================================

  public onPlayerJoined(playerId: string, playerName: string): void {
    const player: Player = { playerId, playerName, joinedAt: Date.now() };
    const newPlayers = { ...this.peerStore.currentState.players, [playerId]: player };
    const newPlayerCount = Object.keys(newPlayers).length;

    this.peerStore.updateState({
      ...this.peerStore.currentState,
      players: newPlayers,
      playerCount: newPlayerCount,
    });

    this.events.onPlayerJoined.emit({ peerId: playerId, metadata: {} });
    this.events.playerCountChanged.emit(newPlayerCount);
  }

  public onPlayerLeft(playerId: string): void {
    const player = this.peerStore.currentState.players[playerId];
    if (!player) return;

    const newPlayers = { ...this.peerStore.currentState.players };
    delete newPlayers[playerId];
    const newPlayerCount = Object.keys(newPlayers).length;

    this.peerStore.updateState({
      ...this.peerStore.currentState,
      players: newPlayers,
      playerCount: newPlayerCount,
    });

    this.events.onPlayerLeft.emit({ peerId: playerId });
    this.events.playerCountChanged.emit(newPlayerCount);
  }

  public clearAllPlayers(): void {
    const hadPlayers = this.peerStore.currentState.playerCount > 0;
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      players: {},
      playerCount: 0,
    });
    if (hadPlayers) {
      this.events.playerCountChanged.emit(0);
    }
  }

  // ========================================
  // Internal - Peer Connected/Disconnected
  // ========================================

  private handlePeerConnected(peerId: string, peerType: PeerType, metadata: PeerMetadata): void {
    if (peerType === 'player') {
      const existingName = this.peerNameMap.get(peerId);
      if (existingName) {
        metadata = { ...metadata, displayName: existingName };
      } else if (!metadata.displayName || metadata.displayName === 'Beacon Player') {
        this.playerCounter++;
        const assignedName = `Player ${this.playerCounter}`;
        this.peerNameMap.set(peerId, assignedName);
        metadata = { ...metadata, displayName: assignedName };
      }
    }

    // Reclaim must keep host-edited permissions; only first connect uses defaults.
    const preserved = this.preservedPermissionsByPeerId.get(peerId);
    if (preserved) {
      this.preservedPermissionsByPeerId.delete(peerId);
    }
    const permissions = preserved ? { ...preserved } : { ...DEFAULT_PERMISSIONS[peerType] };
    const connectedPeer: ConnectedPeer = {
      peerId,
      peerType,
      metadata,
      permissions,
      connectedAt: Date.now(),
    };

    this.peerStore.updateState({
      ...this.peerStore.currentState,
      peers: { ...this.peerStore.currentState.peers, [peerId]: connectedPeer },
    });

    this.typeRegistry.handleConnected(peerType, peerId, metadata);
    this.emitPeerJoinedEvent(peerType, peerId, metadata);

    if (peerType === 'player' && metadata.playjectorSession?.sessionId) {
      this.occupancyTracker?.playerJoinedSession(metadata.playjectorSession.sessionId);
    }

    const connection = this.connectionManager.getConnection(peerId);
    if (connection) {
      this.statsManager.startCollection(peerId, connection);
    }

    if (peerType === 'player') {
      this.updatePlayerCountFromPeers();
    }

    // The connection manager requests the startup IDR when ICE connects. The guest
    // retries only while it has not decoded video; no unconditional backup burst here.
  }

  private handlePeerDisconnected(peerId: string, peerType: PeerType): void {
    const sessionId = this.getPeerPlayjectorSession(peerId)?.sessionId;
    this.silentAudioWatchdog?.clearPeer(peerId);
    this.statsManager.stopCollection(peerId);
    this.typeRegistry.handleDisconnected(peerType, peerId);
    // Stash permissions before store removal when a reclaim hold is armed so the returning
    // guest keeps host edits instead of DEFAULT_PERMISSIONS.
    const existing = this.peerStore.currentState.peers[peerId];
    if (existing && this.connectionManager.hasPeerReconnectHold(peerId)) {
      this.preservedPermissionsByPeerId.set(peerId, { ...existing.permissions });
    } else {
      this.preservedPermissionsByPeerId.delete(peerId);
    }
    this.removePeerFromStore(peerId);
    this.emitPeerLeftEvent(peerType, peerId);
    // NOTE: Do NOT clear recoveryDemotedTierByPeerId here unconditionally. The demotion flag must
    // survive the disconnect→reclaim window so sharedShadowcamFor can hold the camera floor
    // while the peer is mid-reclaim. It is cleared when the reconnect hold expires without
    // reclaim (handleReconnectHoldExpired), or the peer is destroyed by the host (no hold
    // was armed). For host-initiated destroys (kick, stream stop) no hold is armed so the peer
    // cannot reclaim — clear the flag immediately so it doesn't leak.
    if (!this.connectionManager.hasPeerReconnectHold(peerId) && !this.connectionManager.isPeerReconnecting(peerId)) {
      this.recoveryDemotedTierByPeerId.delete(peerId);
    }

    if (peerType === 'player') {
      if (sessionId) {
        if (this.occupancyTracker) {
          // Capture outlives individual disconnects; stop only when the
          // session's last player is gone.
          const remaining = this.occupancyTracker.playerLeftSession(sessionId);
          if (remaining <= 0) this.mediaCoordinator?.stopCastForSession(sessionId);
        } else {
          this.mediaCoordinator?.stopCastForSession(sessionId);
        }
      }
      this.updatePlayerCountFromPeers();
      this.releaseShadowcamDemandForPeer(peerId);
      // Idle-only baseline reset: never promote shared capture while other players remain.
      // Schedule only when no reclaim is in flight / hold armed; hold expiry runs the same
      // path once the guest is truly gone (and only resets when player count is zero).
      if (
        !this.connectionManager.isPeerReconnecting(peerId) &&
        !this.connectionManager.hasReconnectHold()
      ) {
        this.scheduleMaybePromoteSharedShadowcam();
      }
    }

    // Emit allPeersDisconnected when the last connection of any type closes.
    // Skip if the peer is mid-reconnect or in the post-disconnect reclaim hold —
    // guest may re-offer within HOST_PLAYER_RECONNECT_HOLD_MS.
    if (
      this.connectionManager.getConnectionCount() === 0 &&
      !this.connectionManager.isPeerReconnecting(peerId) &&
      !this.connectionManager.hasReconnectHold()
    ) {
      this.events.allPeersDisconnected.emit();
    }
  }

  private removePeerFromStore(peerId: string): void {
    this.connectionFromIds.delete(peerId);
    const peers = { ...this.peerStore.currentState.peers };
    delete peers[peerId];
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      peers,
    });
  }

  /**
   * A player's transport went bad, or the host gave up waiting for its reclaim.
   *
   * Either way the tier is re-evaluated here rather than on the offer path, which cannot do
   * it: an in-place ICE restart keeps the existing peer connection and never re-reads
   * metadata, and a guest that cannot get back sends no offer at all. Demoting from here is
   * what puts a struggling mobile guest on the 360p capture WHILE it is struggling, instead
   * of only once it has already returned.
   */
  private handlePlayerTransportTrouble(peerId: string, metadata: PeerMetadata): void {
    console.log('[PeerCoordinator] Player transport trouble — re-evaluating quality tier', {
      peerId,
      isMobile: metadata.isMobile ?? 'absent',
    });
    // Tier moves exist for players only — an admin/remote-host connection recovering must
    // not swap the shared camera out from under mobile guests.
    const peer = this.peerStore.currentState.peers[peerId];
    if (peer && peer.peerType !== 'player') return;
    // Session peers have their own capture; their link trouble must not re-tier the shared one.
    if (metadata.playjectorSession || peer?.metadata.playjectorSession) return;
    // Demotion needs evidence the LINK failed. A guest whose transport dropped on a clean link
    // - the standing-jitter-buffer refresh above all - keeps its camera: a smaller picture does
    // not cure a receive-side buffer, and the 360p it would land on ramps the same buffer.
    if (!this.recoveryDemotionAllowed(peerId, 'transport-trouble')) return;
    // Only cancel a pending idle baseline reset when recovery will actually demote the camera.
    // A peer already on the bottom tier is a no-op on the camera, so it must not abandon a
    // zero-player reset that would otherwise restore the baseline capture.
    const current = this.mediaCoordinator?.getCurrentShadowcamName() ?? null;
    const recoveryTarget = this.tierSelector.selectRecoveryShadowcam(metadata, current, this.recoveryDemotedTierByPeerId.get(peerId));
    const willDemote = recoveryTarget !== null && recoveryTarget !== current;
    if (willDemote) {
      this.cancelPendingSharedPromote();
    }
    this.scheduleTrackSwapIfNeeded(metadata, { recovery: true, peerId }).catch((err) =>
      console.error('[PeerCoordinator] Recovery track swap failed:', err),
    );
  }

  /**
   * Whether a recovery for `peerId` may step the camera down, from the link record the
   * connection manager kept. Logs the decision and its evidence either way, so a demotion
   * that did or did not happen can be read off the trace with its reason.
   */
  private recoveryDemotionAllowed(peerId: string, trigger: 'transport-trouble' | 'recovery-offer'): boolean {
    const decision = this.connectionManager.recoveryDemotionDecision(peerId);
    const fields = { peerId, trigger, demote: decision.demote, reason: decision.reason, ...decision.evidence };
    console.log('[PeerCoordinator] Recovery demotion', fields);
    if (isDebugWebrtcLoggingEnabled()) {
      sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag: '[RecoveryDemotion]', ...fields }));
    }
    return decision.demote;
  }

  /**
   * Decode-lag episode: record the peer at the bottom tier and swap shared capture to
   * ShadowCam00 (activation → capture → replaceTrackOnAll → IDR). Idempotent when already
   * on a verified ShadowCam00. Missing capture rejects so the effect remains retryable.
   */
  private async handleDecodeLagTierDemotion(
    peerId: string,
    tier: 'bottom',
    cause: QualityTierDemotionCause,
    context?: QualityTierDemotionContext,
  ): Promise<void> {
    const isCurrent = context?.isCurrent ?? (() => true);
    if (!isCurrent()) return;
    // Session peers have their own capture; never swap the shared one on their behalf.
    if (this.getPeerPlayjectorSession(peerId)) return;
    const current = this.mediaCoordinator?.getCurrentShadowcamName() ?? null;
    // Shared-cam gate: desktop-capture sessions have no shadowcam name to swap.
    if (!current) {
      if (this.mediaCoordinator?.getVideoTrack()) {
        // PCM has already applied the per-sender dimension cap before this callback.
        this.recoveryDemotedTierByPeerId.set(peerId, tier);
        return;
      }
      console.log('[PeerCoordinator] Decode-lag demotion unavailable (shared shadowcam inactive)', {
        peerId,
        tier,
        cause,
      });
      throw new Error('Resolution demotion requires an active capture effect');
    }

    const targetShadowcam = QUALITY_TIER_SHADOWCAM[tier];
    this.cancelPendingSharedPromote();
    const applied = await this.runSerializedShadowcamSwap(async () => {
      if (!isCurrent()) return false;
      const media = this.mediaCoordinator;
      if (!media?.getCurrentShadowcamName()) throw new Error('Media capture unavailable');
      console.log('[PeerCoordinator] Applying verified tier demotion swap', { peerId, tier, cause });
      return this.applyVerifiedShadowcam(targetShadowcam, context);
    });
    if (!applied || !isCurrent()) {
      console.log('[PeerCoordinator] Resolution effect cancelled for superseded peer', { peerId, tier });
      return;
    }
    this.recoveryDemotedTierByPeerId.set(peerId, tier);
  }

  /** Called when a reconnect hold window expires without guest reclaim. */
  private handleReconnectHoldExpired(): void {
    // Re-check allPeersDisconnected condition now that holds have cleared.
    if (this.connectionManager.getConnectionCount() === 0 && !this.connectionManager.hasReconnectHold()) {
      this.events.allPeersDisconnected.emit();
    }
    // The reclaim never came — the peer is really gone. Clear the demotion flag for any
    // peers whose hold just expired so sharedShadowcamFor no longer floors the camera on
    // their behalf. The flag survived handlePeerDisconnected for exactly this window.
    this.clearExpiredDemotions();
    // Idle-only: if player count is now zero, reset shared ShadowCam to baseline. Never
    // promote mid-session for remaining guests after a hold expires unclaimed.
    this.scheduleMaybePromoteSharedShadowcam();
  }

  /**
   * Clear demotion flags (and stashed reclaim permissions) for peers that are no longer
   * connected and have no active reclaim hold. Called when holds expire so the camera floor
   * is released and permissions do not leak.
   */
  private clearExpiredDemotions(): void {
    const inStore = new Set(this.getPeersByType('player').map((peer) => peer.peerId));
    for (const peerId of [...this.recoveryDemotedTierByPeerId.keys()]) {
      // Keep the flag if the peer is still connected (still in store)
      if (inStore.has(peerId)) continue;
      // Keep the flag if the peer is mid-replacement or still has a reconnect hold active
      if (this.connectionManager.isPeerReconnecting(peerId)) continue;
      if (this.connectionManager.hasPeerReconnectHold(peerId)) continue;
      // The peer is gone and has no hold — clear its demotion
      this.recoveryDemotedTierByPeerId.delete(peerId);
    }
    for (const peerId of [...this.preservedPermissionsByPeerId.keys()]) {
      if (inStore.has(peerId)) continue;
      if (this.connectionManager.isPeerReconnecting(peerId)) continue;
      if (this.connectionManager.hasPeerReconnectHold(peerId)) continue;
      this.preservedPermissionsByPeerId.delete(peerId);
    }
  }

  /**
   * Called when a reconnect hold is armed (or re-armed).
   * Reschedules the deferred playerCount=0 timer so it stays aligned with the PCM hold.
   */
  private handleReconnectHoldArmed(_peerId: string): void {
    this.rescheduleDeferredZeroPlayerCount();
  }

  /**
   * Called when a peer connection is registered (before ICE/data channel ready).
   * Cancels deferred playerCountChanged(0) immediately so reclaiming peers that
   * started near the end of the hold window do not race the deferred timer.
   */
  private handlePeerConnectionRegistered(peerId: string, peerType: PeerType): void {
    if (peerType === 'player') {
      this.cancelDeferredZeroPlayerCount();
    }
  }

  private updatePlayerCountFromPeers(): void {
    const playerCount = this.getPeersByType('player').length;
    this.peerStore.updateState({
      ...this.peerStore.currentState,
      playerCount,
    });

    if (playerCount > 0) {
      this.cancelDeferredZeroPlayerCount();
      this.events.playerCountChanged.emit(playerCount);
      return;
    }

    // Last player dropped — hold before emitting 0 so Pavilion does not invalidate/stopStream
    // while the guest can still reclaim within HOST_PLAYER_RECONNECT_HOLD_MS.
    if (this.connectionManager.hasReconnectHold()) {
      this.scheduleDeferredZeroPlayerCount();
      return;
    }

    this.events.playerCountChanged.emit(0);
  }

  private scheduleDeferredZeroPlayerCount(): void {
    if (this.deferredZeroPlayerCountTimer) return;
    console.log(
      `[PeerCoordinator] Deferring playerCount=0 for ${HOST_PLAYER_RECONNECT_HOLD_MS}ms (reconnect hold)`,
    );
    this.deferredZeroPlayerCountTimer = setTimeout(() => {
      this.deferredZeroPlayerCountTimer = null;
      const playerCount = this.getPeersByType('player').length;
      this.peerStore.updateState({
        ...this.peerStore.currentState,
        playerCount,
      });
      this.events.playerCountChanged.emit(playerCount);
    }, HOST_PLAYER_RECONNECT_HOLD_MS);
  }

  private cancelDeferredZeroPlayerCount(): void {
    if (!this.deferredZeroPlayerCountTimer) return;
    clearTimeout(this.deferredZeroPlayerCountTimer);
    this.deferredZeroPlayerCountTimer = null;
    console.log('[PeerCoordinator] Cancelled deferred playerCount=0 — player rejoined');
  }

  /**
   * Reschedule the deferred playerCount=0 timer to match a re-armed reconnect hold.
   * Needed when abandonReconnect re-arms the PCM hold but the coordinator's deferred
   * timer was already set from the original disconnect.
   */
  private rescheduleDeferredZeroPlayerCount(): void {
    if (!this.deferredZeroPlayerCountTimer) return;
    clearTimeout(this.deferredZeroPlayerCountTimer);
    this.deferredZeroPlayerCountTimer = null;
    console.log('[PeerCoordinator] Rescheduling deferred playerCount=0 to match re-armed hold');
    this.scheduleDeferredZeroPlayerCount();
  }

  // ========================================
  // Internal - Peer ID & ICE Configuration
  // ========================================

  private handlePeerIdAssigned(data: PeerIdAssignedData): void {
    const { peerId, peerIdType, iceServers } = data;

    if (peerIdType === 'host') {
      this.updateHostPeerId(peerId);
    } else if (peerIdType === 'remoteHost') {
      this.updateRemoteHostPeerId(peerId);
    }

    if (iceServers.length > 0) {
      this.connectionManager.setIceConfig({
        iceServers,
        iceTransportPolicy: this.iceTransportPolicy,
      });
      console.log(`[PeerCoordinator] ICE config updated (${iceServers.length} servers, policy=${this.iceTransportPolicy})`);

      if (!this.connectionManager.hasPreWarmedConnection()) {
        this.connectionManager.preWarm();
      }
    }
  }

  // ========================================
  // Internal - Signaling
  // ========================================

  private handleSignalGenerated(peerId: string, description: RTCSessionDescriptionInit): void {
    // Session-bound connections signal from their instance peer (recorded at
    // accept time); legacy peers fall back to the v1 global host peer.
    const fromId = this.connectionFromIds.get(peerId) ?? this.peerStore.currentState.hostPeerId;
    if (!fromId || !this.signalingService) {
      sentryLog.warn(
        `[PeerCoordinator] Dropping outbound ${description.type} for ${peerId}: no host peer identity to send from`,
      );
      return;
    }

    if (description.type === 'offer') {
      this.signalingService.sendOffer(fromId, peerId, description);
    } else if (description.type === 'answer') {
      this.signalingService.sendAnswer(fromId, peerId, description);
    }
  }

  private handleIceCandidatesGenerated(peerId: string, candidates: RTCIceCandidate[]): void {
    const fromId = this.connectionFromIds.get(peerId) ?? this.peerStore.currentState.hostPeerId;
    if (!fromId || !this.signalingService) {
      sentryLog.warn(
        `[PeerCoordinator] Dropping outbound ICE candidates for ${peerId}: no host peer identity to send from`,
      );
      return;
    }

    this.signalingService.sendIceCandidate(fromId, peerId, candidates);
  }

  /**
   * A rejected signaling handler drops the guest's offer on the floor: the guest waits
   * for an answer that never arrives and only recovers via reconnect. The likeliest
   * source is a ShadowCam capture-verify throw propagating out of acceptIncomingPeer,
   * which is otherwise invisible because only the audio capture path reports to Sentry.
   */
  private reportSignalingFailure(type: string, fromId: string, error: unknown): void {
    console.error(`${DIAG} Signaling message handling failed type=${type} fromId=${fromId}:`, error);
    Sentry.captureException(error, {
      tags: { peer_stage: 'signaling_message', signaling_message_type: type },
      contexts: { signaling: { type, fromId } },
    });
  }

  private async handleSignalingMessage(msg: { type: string; fromId: string; toId: string; sdp?: RTCSessionDescriptionInit; candidates?: RTCIceCandidateInit[]; peerMetadata?: PeerMetadata; peerIdType?: string }): Promise<void> {
    sentryLog.info(
      `[PeerCoordinator] handleSignalingMessage type=${msg.type} from=${msg.fromId} to=${msg.toId} peerIdType=${msg.peerIdType ?? 'none'}`,
    );
    const hostPeerId = this.peerStore.currentState.hostPeerId;
    if (hostPeerId && msg.toId && msg.toId !== hostPeerId && !this.instancePeerIds.has(msg.toId)) {
      console.warn('[PeerCoordinator] Ignoring signaling message with mismatched toId', {
        type: msg.type,
        fromId: msg.fromId,
        toId: msg.toId,
        hostPeerId,
      });
      return;
    }

    if (msg.type === 'offer' && msg.sdp) {
      let metadata: PeerMetadata = this.captureMetadata(msg.peerMetadata ?? {});
      const peerType = this.mapPeerIdTypeToPeerType(msg.peerIdType) ?? 'player';
      // Read before accepting the offer: createHostSideConnection registers the replacement
      // connection and clears the reconnect marker this decision depends on.
      //
      // An ICE restart offer (same SDP session ID, just new ICE credentials) should
      // NOT be treated as a recovery that demotes cameras. Only full reconnect offers
      // (new SDP session) should trigger recovery tier logic.
      const isKnownPeer = this.connectionManager.isReofferFromKnownPeer(msg.fromId);
      const isIceRestart = this.connectionManager.isIceRestartOffer(msg.fromId, msg.sdp);
      const isRecoveryOffer = isKnownPeer && !isIceRestart;

      if (isIceRestart) {
        if (peerType === 'player') {
          await this.scheduleTrackSwapIfNeeded(metadata, { peerId: msg.fromId });
        }
        await this.acceptIncomingPeer(msg.fromId, peerType, metadata, msg.sdp, null, null);
        return;
      }

      if (peerType === 'player' && !metadata.playjectorSession && this.sessionResolver) {
        const instanceIndex = this.instancePeerIds.get(msg.toId);
        sentryLog.info(
          `[PeerCoordinator] Instance lookup: toId=${msg.toId} instanceIndex=${instanceIndex ?? 'not-found'} registeredPeers=${this.instancePeerIds.size}`,
        );
        if (instanceIndex !== undefined) {
          const session = this.sessionResolver.getSessionForInstance(instanceIndex);
          if (!session) {
            sentryLog.warn(
              `[PeerCoordinator] Refusing ${msg.fromId}: no ready session for instance ${instanceIndex}`,
            );
            return;
          }
          sentryLog.info(
            `[PeerCoordinator] Resolved session for instance ${instanceIndex}: sessionId=${session.sessionId}`,
          );
          metadata = { ...metadata, playjectorSession: session };
        }
      }

      // Race guard: matchmakeable=false may not have propagated before the player was matched.
      if (this.refuseOfferIfShadowcamUnhealthy(msg.fromId, peerType)) {
        return;
      }

      await this.runSerializedShadowcamSwap(() => this.ensureMediaCapture(metadata, peerType));
      if (peerType === 'player' && !metadata.playjectorSession && this.mediaCoordinator && !this.mediaCoordinator.getVideoTrack()) {
        throw new Error('Player capture unavailable');
      }

      // Select and await the mobile/shared capture before the sender or answer is created.
      // An asynchronous post-answer swap used to send the cached desktop track first.
      if (peerType === 'player') {
        this.cancelPendingSharedPromote();
        const recovery = isRecoveryOffer && this.recoveryDemotionAllowed(msg.fromId, 'recovery-offer');
        await this.scheduleTrackSwapIfNeeded(metadata, { recovery, peerId: msg.fromId });
      }

      const sessionId = metadata.playjectorSession?.sessionId;
      const videoTrack = sessionId
        ? this.mediaCoordinator?.getVideoTrackForSession(sessionId) ?? null
        : this.mediaCoordinator?.getVideoTrack() ?? null;
      const audioTrack = sessionId
        ? this.mediaCoordinator?.getAudioTrackForSession(sessionId) ?? null
        : this.mediaCoordinator?.getAudioTrack() ?? null;
      const audioDetails = audioTrack
        ? { label: audioTrack.label, readyState: audioTrack.readyState, enabled: audioTrack.enabled, muted: audioTrack.muted }
        : null;
      sentryLog.info(
        `[PeerCoordinator] Incoming offer from=${msg.fromId} type=${peerType} hasVideo=${!!videoTrack} hasAudio=${!!audioTrack} sessionId=${sessionId ?? 'none'}`,
      );
      Sentry.addBreadcrumb({
        category: 'AUDIO-BUG-12009',
        message: `Offer received, sending tracks to peer`,
        level: audioTrack ? 'info' : 'warning',
        data: { fromId: msg.fromId, peerType, hasVideo: !!videoTrack, hasAudio: !!audioTrack, ...audioDetails },
      });

      // The selected capture is ready before the answer can enable its video sender.
      await this.acceptIncomingPeer(msg.fromId, peerType, metadata, msg.sdp, videoTrack, audioTrack);
    } else if (msg.type === 'answer' && msg.sdp) {
      sentryLog.info(`[PeerCoordinator] SDP answer from=${msg.fromId}`);
      if (!this.connectionManager.getConnection(msg.fromId)) {
        console.warn(`[PeerCoordinator] Ignoring answer from unknown peer ${msg.fromId}`);
        return;
      }
      await this.connectionManager.handleRemoteSdp(msg.fromId, msg.sdp);
    } else if (msg.type === 'ice-candidate' && msg.candidates) {
      sentryLog.info(`[PeerCoordinator] ICE candidates from=${msg.fromId} count=${msg.candidates.length}`);
      // Allow handleRemoteIceCandidate to buffer candidates even when the connection
      // doesn't exist yet — the offer handler may be awaiting ensureMediaCapture for
      // seconds while ICE trickles in. drainPendingIceCandidates applies them after
      // the connection registers.
      for (const candidateInit of msg.candidates) {
        await this.connectionManager.handleRemoteIceCandidate(
          msg.fromId,
          new RTCIceCandidate(candidateInit),
        );
      }
    }
  }

  // ========================================
  // Internal - Media Capture
  // ========================================

  private async ensureMediaCapture(metadata?: PeerMetadata, peerType?: PeerType): Promise<void> {
    if (!this.mediaCoordinator) {
      console.warn('[PeerCoordinator] No MediaCoordinator available for capture');
      return;
    }

    const session = metadata?.playjectorSession;
    const sessionId = session?.sessionId;
    const hasVideo = sessionId
      ? !!this.mediaCoordinator.getVideoTrackForSession(sessionId)
      : !!this.mediaCoordinator.getVideoTrack();
    const hasAudio = sessionId
      ? !!this.mediaCoordinator.getAudioTrackForSession(sessionId)
      : !!this.mediaCoordinator.getAudioTrack();

    if (peerType === 'admin') {
      // Start audio before ShadowCam awaits so camera stalls do not delay game audio.
      const audioPromise = !hasAudio
        ? this.captureGameAudioWithRenderPath('admin')
        : Promise.resolve(null);
      if (hasVideo) {
        // A shared capture is already live — possibly tiered or recovery-demoted for mobile
        // guests. Recapturing the desktop cam here would force-promote every connected guest
        // to 720p mid-session (tracksChanged → replaceTrackOnAll), and nothing would ever
        // swap them back. The admin joins the stream as it is.
        console.log('[PeerCoordinator] Admin session: reusing live capture (no forced desktop recapture)');
      } else {
        if (this.beforeAdminMediaCapture) {
          console.log('[PeerCoordinator] Admin session: waiting for ShadowCam pipeline refresh before capture');
          await this.beforeAdminMediaCapture().catch((err) =>
            console.warn('[PeerCoordinator] Admin ShadowCam refresh failed; capturing anyway', err),
          );
        }
        console.log(`[PeerCoordinator] Admin session: capturing ${ADMIN_SHADOWCAM} (desktop virtual display)`);
        await this.ensureActivation(ADMIN_SHADOWCAM);
        const adminVideoResult = await this.mediaCoordinator.startShadowcamCapture(ADMIN_SHADOWCAM);
        this.logVideoCaptureResult(adminVideoResult, 'admin', ADMIN_SHADOWCAM);
      }
      if (!hasAudio) {
        const audioResult = await audioPromise;
        this.logAudioCaptureResult(audioResult, 'admin');
      }
      console.log('[PeerCoordinator] Media capture complete (admin)', {
        hasVideo: !!this.mediaCoordinator.getVideoTrack(),
        hasAudio: !!this.mediaCoordinator.getAudioTrack(),
      });
      return;
    }

    if (sessionId) {
      // Android multi-instance host Phase 5: on the android path the instance's
      // camera is activated BY capture (streams model) — the host must not send
      // explicit bind/activate messages for it. Legacy/mumu sessions keep the
      // explicit activation below.
      const androidStreamActive = this.getActiveAndroidStreamId?.() !== undefined;
      let activationReady = true;
      if (session.shadowCamName) {
        // Session cameras are owned by the session capture lifecycle, never by the
        // on-demand shared-tier teardown.
        this.sessionShadowcams.add(session.shadowCamName);
        if (!androidStreamActive) {
          activationReady = await this.ensureActivation(session.shadowCamName);
        }
      }
      const result = await this.mediaCoordinator.startCastForSession(sessionId, {
        shadowCamName: session.shadowCamName,
        audioDeviceName: session.audioDeviceName,
      });
      if (session.shadowCamName) {
        this.logVideoCaptureResult(
          result.videoTrack,
          peerType ?? 'player',
          session.shadowCamName,
        );
      }
      if (!activationReady || result.readiness.status === 'DRIVER_BLOCKED') {
        console.warn(
          `[PeerCoordinator] Game instance session ${sessionId} is not ready: ` +
          `${result.readiness.status === 'DRIVER_BLOCKED'
            ? result.readiness.reason
            : 'ShadowCam activation timed out'}`,
        );
      }
      if (result.audio.status === 'DRIVER_BLOCKED') {
        console.warn(`[PeerCoordinator] Game instance audio unavailable for ${sessionId}: ${result.audio.reason}`);
      }
      return;
    }

    if (hasVideo && hasAudio) {
      console.log('[PeerCoordinator] Tracks already captured, skipping');
      return;
    }

    // Start AudioCaster capture in parallel with ShadowCam warm-up so DISPLAY capture
    // stalls cannot delay game audio (AUDIO-BUG-12009 lifecycle).
    const audioPromise = !hasAudio
      ? this.captureGameAudioWithRenderPath(peerType ?? 'unknown')
      : Promise.resolve(null);

    if (!hasVideo) {
      // Lowest common tier, not this peer's own: a fresh capture installs one track for
      // every connected guest, so picking the newcomer's tier here would promote a
      // recovery-demoted mobile guest right back up - the same hazard the admin branch
      // above refuses to create.
      const shadowcam = !peerType && !metadata && this.getPlayerCount() === 0
        ? this.selectShadowcam({ isMobile: false }) : this.sharedShadowcamFor(metadata);
      // This runs inside the offer handler, where a throw drops the guest's offer and it waits
      // for an answer that never arrives (see reportSignalingFailure). Activation is a
      // best-effort camera warm-up, so its failure must degrade the picture, not the connection.
      await this.ensureShadowcamActivated(shadowcam).catch((err) =>
        console.warn(`[PeerCoordinator] Capturing ${shadowcam} despite activation failure`, err),
      );
      console.log(`[PeerCoordinator] Capturing shadowcam (${shadowcam})...`);
      const videoResult = await this.mediaCoordinator.startShadowcamCapture(shadowcam);
      this.logVideoCaptureResult(videoResult, peerType ?? 'unknown', shadowcam);
    }

    if (!hasAudio) {
      const audioResult = await audioPromise;
      this.logAudioCaptureResult(audioResult, peerType ?? 'unknown');
    }

    console.log('[PeerCoordinator] Media capture complete', {
      hasVideo: !!this.mediaCoordinator.getVideoTrack(),
      hasAudio: !!this.mediaCoordinator.getAudioTrack(),
    });
  }

  /**
   * Attempt to restore AudioCaster's render path, then capture game audio even if
   * that restoration rejects. Returns null without a media coordinator or available
   * audio track; errors from startGameAudioCapture, including enumeration, propagate.
   */
  private async captureGameAudioWithRenderPath(peerType: string): Promise<MediaStreamTrack | null> {
    if (!this.mediaCoordinator) return null;
    if (this.ensureAudioCasterDefault) {
      console.log(`${DIAG} Ensuring AudioCaster default render path for ${peerType}...`);
      await this.ensureAudioCasterDefault().catch((err) =>
        console.warn(`${DIAG} ensureAudioCasterDefault failed; capturing anyway`, err),
      );
    }
    console.log(`${DIAG} Capturing AudioCaster (game audio) for ${peerType}...`);
    return this.mediaCoordinator.startGameAudioCapture();
  }

  /**
   * Before a peer connection is accepted, apply and verify its bounded shared capture.
   * Uses replaceTrack() which preserves all sender parameters (bitrate, codec, etc.)
   * without renegotiation.
   *
   * `recovery` marks a re-offer / host recovery rather than a first connect, which is the
   * only situation that may step a mobile peer down a tier. Camera changes happen here and
   * nowhere else: there is no mid-session swap on a working stream.
   */
  private async scheduleTrackSwapIfNeeded(
    metadata?: PeerMetadata,
    options: { recovery?: boolean; peerId?: string } = {},
  ): Promise<void> {
    if (!this.mediaCoordinator) return;
    if (metadata?.playjectorSession) return;

    const current = this.mediaCoordinator.getCurrentShadowcamName();
    if (!current) return;

    const recovery = options.recovery === true;
    await this.runSerializedShadowcamSwap(async () => {
      const media = this.mediaCoordinator;
      if (!media) throw new Error('Media coordinator unavailable');
      // Re-read under the lock: a swap that ran ahead of us may have already moved the camera,
      // so the tier decision must be made against the camera actually in use right now.
      const currentInLock = media.getCurrentShadowcamName();
      if (!currentInLock) throw new Error('Shared capture changed while selecting resolution');

      // A recovery tier the camera in use cannot express falls back to the metadata-derived
      // initial tier, which is the only other evidence available.
      //
      // A first connect takes the LOWEST COMMON tier across every player present, not just
      // the arriving peer's own. The camera is shared, so seating a newcomer on its own tier
      // re-seats everyone already on it: a desktop player joining used to pull the shared
      // capture up to 720p on top of a mobile guest that a connection failure had just
      // stepped down to 360p, undoing the demotion for as long as they overlapped. Demoted
      // peers count as questionable in that tally, so the floor holds. Falls back to the
      // arriving peer's tier when the store has no players yet to compare against.
      const retainedTier = options.peerId ? this.recoveryDemotedTierByPeerId.get(options.peerId) : undefined;
      let neededShadowcam = recovery
        ? (this.tierSelector.selectRecoveryShadowcam(metadata, currentInLock, retainedTier) ??
          this.selectShadowcam(metadata))
        : this.sharedShadowcamFor(metadata);
      if (this.connectionManager.getConnectionCount() > 0 && this.tierSelector.shouldPromoteSharedShadowcam(currentInLock, neededShadowcam)) {
        neededShadowcam = currentInLock;
      }
      const demotedPeerId = recovery ? options.peerId : undefined;
      const demotedTier = tierForShadowcam(neededShadowcam);
      const currentTrack = media.getVideoTrack();
      if (currentInLock === neededShadowcam && this.shadowcamTrackVerified(neededShadowcam, currentTrack)) {
        console.log('[PeerCoordinator] Shadowcam swap skipped — tier unchanged', {
          current: currentInLock, needed: neededShadowcam, recovery,
          isMobile: metadata?.isMobile ?? 'absent',
        });
        return;
      }
      await this.applyVerifiedShadowcam(neededShadowcam);
      if (demotedPeerId && demotedTier) {
        this.recoveryDemotedTierByPeerId.set(demotedPeerId, demotedTier);
      }
    });
  }

  /**
   * Non-throwing wrapper over ensureShadowcamActivated.
   * Legacy pavilion/admin flows intentionally degrade on failure; Game instance callers
   * retain the boolean to report a structured readiness failure.
   */
  private async ensureActivation(shadowcamName: string): Promise<boolean> {
    try {
      await this.ensureShadowcamActivated(shadowcamName);
      return true;
    } catch {
      // ensureShadowcamActivated already logged the failure.
      return false;
    }
  }

  /** Caller holds the capture swap lock. A label alone is never verified application. */
  private async applyVerifiedShadowcam(shadowcam: string, context?: QualityTierDemotionContext): Promise<boolean> {
    const lifecycle = this.captureLifecycle;
    const media = this.mediaCoordinator;
    const isCurrent = () => lifecycle === this.captureLifecycle && media === this.mediaCoordinator && context?.isCurrent?.() !== false;
    if (!isCurrent()) return false;
    const oldTrack = media?.getVideoTrack();
    if (!media || !oldTrack || !profileForShadowcamName(shadowcam)) throw new Error('Resolution capture unavailable');
    const request = { isCurrent, requestId: context?.requestId ?? `capture-${++this.captureRequestSequence}`,
      hostDeadlineMs: context?.hostDeadlineMs ?? Date.now() + 5000 };
    if (media.getCurrentShadowcamName() === shadowcam && this.shadowcamTrackVerified(shadowcam, oldTrack)) {
      await this.connectionManager.replaceTrackOnAll(oldTrack, oldTrack, request);
      return true;
    }
    await ShadowcamCaptureManager.beforeDeadline(this.ensureShadowcamActivated(shadowcam), request, request.hostDeadlineMs);
    const prepared = await media.prepareShadowcamCapture(shadowcam, request);
    if (!prepared) throw new Error('Resolution capture unavailable');
    try {
      if (!isCurrent()) throw new Error('Capture superseded');
      if (!this.shadowcamTrackVerified(shadowcam, prepared.track)) throw new Error('Resolution capture profile did not verify');
      await this.connectionManager.replaceTrackOnAll(oldTrack, prepared.track, { ...request,
        commit: () => { media.commitPreparedShadowcam(shadowcam, prepared, request); } });
      if (oldTrack !== prepared.track) media.retireVideoTrack(oldTrack);
      return true;
    } catch (error) {
      media.discardPreparedShadowcam(prepared);
      throw error;
    }
  }

  /** Exact ShadowCam resolution; the rate is whatever the capture open negotiated. */
  private shadowcamTrackVerified(shadowcam: string, track: MediaStreamTrack | null | undefined): boolean {
    return !!track && trackMeetsRequestedShadowcam(track, shadowcam);
  }

  private cancelPendingSharedPromote(): void {
    if (this.pendingSharedPromoteTimer) {
      clearTimeout(this.pendingSharedPromoteTimer);
      this.pendingSharedPromoteTimer = null;
    }
    // Increment the generation token so any in-flight async idle baseline reset is abandoned.
    this.sharedPromoteGeneration++;
  }

  /** Tier inputs for the currently connected players, including recovery demotion flags. */
  /**
   * The camera tier a player should be seated on, taking every player into account.
   *
   * The capture is SHARED, so seating an arriving peer on its own tier re-seats everyone
   * already on it. Taking the lowest common tier is what stops a desktop player joining
   * from pulling the shared capture up to 720p on top of a mobile guest that a connection
   * failure had just stepped down to 360p. Demoted peers count as questionable in the
   * tally, so the floor holds for as long as they are connected.
   *
   * The arriving peer is passed in because it is not registered in the store yet.
   */
  private sharedShadowcamFor(metadata?: PeerMetadata): string {
    const inStore = new Set(this.getPeersByType('player').map((peer) => peer.peerId));
    // A demoted peer that is mid-reclaim has been removed from the store but has NOT left,
    // and the store-driven tally would therefore ignore it at the one moment the floor
    // matters most: another guest arriving during that window would capture a higher tier
    // and the returning peer would land on it. Its demotion is remembered until it either
    // comes back or is written off, so hold the floor from that memory instead.
    const demotedMidReclaim = [...this.recoveryDemotedTierByPeerId.entries()]
      .filter(([peerId]) => !inStore.has(peerId))
      .map(([, demotedTier]) => ({ demotedTier }));

    return (
      this.tierSelector.selectSharedShadowcam([
        ...this.currentPlayerTierInputs(),
        ...demotedMidReclaim,
        {
          isMobile: this.tierSelector.effectiveIsMobile(metadata),
          connectionQuestionable: metadata?.connectionQuestionable === true,
        },
      ]) ?? this.selectShadowcam(metadata)
    );
  }

  /**
   * Players riding the shared (tiered) capture. Game instance session peers have their own
   * per-session capture, so they never count toward the shared tier tally, idle checks, or
   * on-demand shadowcam teardown.
   */
  private sharedCapturePlayers(): ConnectedPeer[] {
    return this.getPeersByType('player').filter((peer) => !peer.metadata.playjectorSession);
  }

  private currentPlayerTierInputs(): SharedTierInput[] {
    return this.sharedCapturePlayers().map((peer) => ({
      isMobile: this.tierSelector.effectiveIsMobile(peer.metadata),
      connectionQuestionable: peer.metadata.connectionQuestionable === true,
      demotedTier: this.recoveryDemotedTierByPeerId.get(peer.peerId),
    }));
  }

  /**
   * Runs a shadowcam capture/replace critical section with no other swap in flight, so two
   * captures can never install-and-stop each other's track. The chain continues even when a
   * task rejects, so one failure cannot wedge every future swap.
   */
  private runSerializedShadowcamSwap<T>(task: () => Promise<T>): Promise<T> {
    const run = this.shadowcamSwapChain.then(task, task);
    this.shadowcamSwapChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  private scheduleMaybePromoteSharedShadowcam(): void {
    this.cancelPendingSharedPromote();
    this.pendingSharedPromoteTimer = setTimeout(() => {
      this.pendingSharedPromoteTimer = null;
      this.maybePromoteSharedShadowcam();
    }, 0);
  }

  /**
   * Idle-only shared ShadowCam policy.
   *
   * NEVER promote mid-session because a peer left or a reconnect hold expired while other
   * players remain. ONLY reset shared ShadowCam to baseline when connected player count === 0.
   *
   * Used by both the schedule-time check and the recompute under the swap lock so they cannot
   * drift (they used to be separate expressions).
   *
   * With nobody connected there is no lowest common denominator to compute. Returning null
   * there left the shared camera wherever the last guest put it, so an idle stream sat on 360p
   * and handed that down to whoever connected next. An unconstrained stream returns to the
   * baseline tier.
   *
   * Idle means NOTHING IS CONNECTED, and nothing else. It deliberately does not consult the
   * demotion memory: a stream with no connections resets immediately, so the next guest to
   * arrive is judged on its own link rather than inheriting the last one's bad night.
   */
  private sharedShadowcamForRemainingPlayers(): string | null {
    const players = this.currentPlayerTierInputs();
    const idle = players.length === 0;
    return idle ? this.selectShadowcam({ isMobile: false }) : this.tierSelector.selectSharedShadowcam(players);
  }

  private maybePromoteSharedShadowcam(): void {
    if (!this.mediaCoordinator) return;
    const current = this.mediaCoordinator.getCurrentShadowcamName();
    if (!current) return;

    // Mid-session: never raise shared capture while any player remains connected.
    if (this.sharedCapturePlayers().length > 0) {
      return;
    }

    // Nothing is connected, so there is no demotion left to honour. Dropping the memory here
    // rather than waiting for a reconnect hold to lapse is what makes the reset immediate, and
    // it keeps a stale entry from flooring the next guest to arrive.
    if (this.recoveryDemotedTierByPeerId.size > 0) {
      console.log('[PeerCoordinator] Stream idle — clearing recovery demotions', {
        cleared: this.recoveryDemotedTierByPeerId.size,
      });
      this.recoveryDemotedTierByPeerId.clear();
    }

    const needed = this.sharedShadowcamForRemainingPlayers();
    if (!this.tierSelector.shouldPromoteSharedShadowcam(current, needed) || !needed) {
      return;
    }

    // Capture the generation token before queueing. A demotion increments it (via
    // cancelPendingSharedPromote), which abandons this idle reset whether it is still queued
    // behind the demotion's swap or already past the lock.
    const startGeneration = this.sharedPromoteGeneration;

    void this.runSerializedShadowcamSwap(async () => {
      // A demotion that ran (or was scheduled) while we waited for the swap lock supersedes
      // this idle reset: stepping the camera back up would immediately undo its demotion.
      if (this.sharedPromoteGeneration !== startGeneration) {
        console.log('[PeerCoordinator] Abandoning stale shared shadowcam idle reset (demotion intervened)');
        return;
      }
      const media = this.mediaCoordinator;
      if (!media) return;
      // Re-read under the lock: the target tier must still be an upgrade over the camera in use.
      const currentInLock = media.getCurrentShadowcamName();
      if (!currentInLock) return;
      // Still idle under the lock — a peer may have connected while we waited.
      if (this.sharedCapturePlayers().length > 0) {
        return;
      }
      // Recompute the baseline under the lock too.
      const neededInLock = this.sharedShadowcamForRemainingPlayers();
      if (!neededInLock || !this.tierSelector.shouldPromoteSharedShadowcam(currentInLock, neededInLock)) {
        return;
      }

      await this.applyVerifiedShadowcam(neededInLock, {
        isCurrent: () => this.sharedPromoteGeneration === startGeneration && this.sharedCapturePlayers().length === 0,
      });
      // After a successful idle reset, deactivate on-demand cameras that are no longer needed.
      // The old camera (oldCam) was skipped by releaseShadowcamDemandForPeer because it was
      // the inUseCam at leave time; now that we've switched away, release it.
      this.releaseUnneededShadowcams();
    }).catch((err) =>
      console.error('[PeerCoordinator] Shared shadowcam idle reset failed:', err),
    );
  }

  /**
   * Deactivate on-demand ShadowCams that no remaining peer needs. Called after a successful
   * idle baseline reset to clean up cameras that were skipped by releaseShadowcamDemandForPeer
   * because they were the inUseCam at leave time. Desktop Cam03 stays always-on.
   *
   * Unlike releaseShadowcamDemandForPeer, this runs synchronously inside the swap mutex
   * (caller already holds the lock) and skips queueing another swap.
   */
  private releaseUnneededShadowcams(): void {
    const remaining = this.sharedCapturePlayers();
    const stillNeeded = new Set<string>();
    for (const peer of remaining) {
      stillNeeded.add(
        this.selectShadowcam({
          isMobile: this.tierSelector.effectiveIsMobile(peer.metadata),
          connectionQuestionable:
            peer.metadata.connectionQuestionable === true ||
            this.recoveryDemotedTierByPeerId.has(peer.peerId),
        }),
      );
    }
    const shared = this.tierSelector.selectSharedShadowcam(this.currentPlayerTierInputs());
    if (shared) stillNeeded.add(shared);

    const inUseCam = this.mediaCoordinator?.getCurrentShadowcamName();
    for (const cam of [...this.activatedShadowcams]) {
      if (cam === DESKTOP_SHADOWCAM || this.sessionShadowcams.has(cam)) continue;
      if (stillNeeded.has(cam)) continue;
      if (cam === inUseCam) continue;
      // Cancel any pending deactivation since we're deactivating now
      this.pendingDeactivations.delete(cam);
      this.activatedShadowcams.delete(cam);
      console.log(`[PeerCoordinator] Deactivating on-demand shadowcam after idle reset: ${cam}`);
      void this.deactivateShadowcam?.(cam)?.catch((err) =>
        console.error(`[PeerCoordinator] Shadowcam deactivation failed: ${cam}`, err),
      );
    }
  }

  /**
   * After a player leaves, tear down on-demand ShadowCams that no remaining peer needs.
   * Desktop Cam03 stays always-on. Runs through the swap mutex to avoid racing an in-flight
   * capture/replaceTrack that may still be using the camera being deactivated.
   */
  private releaseShadowcamDemandForPeer(_peerId: string): void {
    const remaining = this.sharedCapturePlayers();
    const stillNeeded = new Set<string>();
    for (const peer of remaining) {
      stillNeeded.add(
        this.selectShadowcam({
          isMobile: this.tierSelector.effectiveIsMobile(peer.metadata),
          connectionQuestionable:
            peer.metadata.connectionQuestionable === true ||
            this.recoveryDemotedTierByPeerId.has(peer.peerId),
        }),
      );
    }
    const shared = this.tierSelector.selectSharedShadowcam(this.currentPlayerTierInputs());
    if (shared) stillNeeded.add(shared);

    const camsToDeactivate: string[] = [];
    const inUseCam = this.mediaCoordinator?.getCurrentShadowcamName();
    for (const cam of [...this.activatedShadowcams]) {
      if (cam === DESKTOP_SHADOWCAM || this.sessionShadowcams.has(cam)) continue;
      if (stillNeeded.has(cam)) continue;
      // Never deactivate the camera we are still capturing from: startStopToggle(false) only
      // pauses frame delivery while the browser track stays 'live' on the last frame, and the
      // rejoin path checks track truthiness only — a same-tier joiner would get a frozen frame.
      if (cam === inUseCam) continue;
      camsToDeactivate.push(cam);
    }

    if (camsToDeactivate.length === 0) return;

    // Mark cameras as pending deactivation so ensureShadowcamActivated can cancel if a guest joins.
    for (const cam of camsToDeactivate) {
      this.pendingDeactivations.add(cam);
    }

    // Run deactivation through the swap mutex so it cannot race an in-flight capture/replace.
    // Re-check demand inside the mutex: a guest may have joined between scheduling and execution.
    void this.runSerializedShadowcamSwap(async () => {
      for (const cam of camsToDeactivate) {
        // Skip if activation cancelled the pending deactivation (mid-handshake peer joined).
        if (!this.pendingDeactivations.delete(cam)) {
          console.log(`[PeerCoordinator] Skipping deactivation of ${cam} — cancelled by activation`);
          continue;
        }
        // Re-evaluate demand under the lock: a new peer may have activated this camera
        // while we waited for the mutex. Skip deactivation if the cam is now needed.
        if (this.isShadowcamStillNeeded(cam)) {
          console.log(`[PeerCoordinator] Skipping deactivation of ${cam} — now needed by a peer`);
          continue;
        }
        this.activatedShadowcams.delete(cam);
        await this.deactivateShadowcam?.(cam)?.catch((err) =>
          console.error(`[PeerCoordinator] Shadowcam deactivation failed: ${cam}`, err),
        );
      }
    });
  }

  /** Returns true if any connected player needs the given shadowcam. */
  private isShadowcamStillNeeded(cam: string): boolean {
    if (cam === DESKTOP_SHADOWCAM || this.sessionShadowcams.has(cam)) return true;
    // The camera currently feeding the capture pipeline is always "needed" — see the
    // frozen-frame note in releaseShadowcamDemandForPeer.
    if (cam === this.mediaCoordinator?.getCurrentShadowcamName()) return true;
    const remaining = this.sharedCapturePlayers();
    for (const peer of remaining) {
      const needed = this.selectShadowcam({
        isMobile: this.tierSelector.effectiveIsMobile(peer.metadata),
        connectionQuestionable:
          peer.metadata.connectionQuestionable === true ||
          this.recoveryDemotedTierByPeerId.has(peer.peerId),
      });
      if (needed === cam) return true;
    }
    const shared = this.tierSelector.selectSharedShadowcam(this.currentPlayerTierInputs());
    return shared === cam;
  }

  /**
   * Resolves once Playjector has started the camera *and* bound a display source to it. The device
   * is enumerable for getUserMedia well before that, and capturing early hands the guest the
   * camera's green placeholder, so callers must await this before capture. Concurrent peers needing
   * the same camera share one activation.
   */
  private async ensureShadowcamActivated(shadowcamName: string): Promise<void> {
    // If a deactivation is queued but not yet executed, cancel it — the cam is still live.
    if (this.pendingDeactivations.delete(shadowcamName)) {
      console.log(`[PeerCoordinator] Cancelled pending deactivation for ${shadowcamName}`);
    }
    if (this.activatedShadowcams.has(shadowcamName)) return;
    if (!this.activateShadowcam) return;

    const inFlight = this.shadowcamActivations.get(shadowcamName);
    if (inFlight) return inFlight;

    const activation = this.activateShadowcam(shadowcamName)
      .then(() => {
        // Only mark active after the activate callback resolves (bind confirmed / polled).
        this.activatedShadowcams.add(shadowcamName);
      })
      .catch((err) => {
        console.error(`[PeerCoordinator] Shadowcam activation failed: ${shadowcamName}`, err);
        throw err;
      })
      .finally(() => {
        this.shadowcamActivations.delete(shadowcamName);
      });

    this.shadowcamActivations.set(shadowcamName, activation);
    return activation;
  }

  private logAudioCaptureResult(track: MediaStreamTrack | null, peerType: string): void {
    if (track) {
      console.log(`${DIAG} Audio capture SUCCESS for ${peerType}`, {
        label: track.label, readyState: track.readyState, enabled: track.enabled,
      });
    } else {
      console.error(`${DIAG} Audio capture FAILED for ${peerType} — track is null`);
      Sentry.captureMessage('[AUDIO-BUG-12009] Game audio capture returned null', {
        level: 'warning',
        tags: { audio_stage: 'ensure_capture', audio_diag: 'AUDIO-BUG-12009', peer_type: peerType },
      });
    }
  }

  /** Video-pipeline mirror of logAudioCaptureResult. */
  private logVideoCaptureResult(
    track: MediaStreamTrack | null,
    peerType: string,
    shadowcam: string,
  ): void {
    if (track) {
      console.log(`[BLUE-STREAM-12010:PeerCoordinator] Shadowcam capture SUCCESS for ${peerType}`, {
        shadowcam, label: track.label, readyState: track.readyState, enabled: track.enabled,
      });
      return;
    }
    console.error(`[BLUE-STREAM-12010:PeerCoordinator] Shadowcam capture FAILED for ${peerType} — track is null`);
    Sentry.captureMessage('[BLUE-STREAM-12010] Shadowcam capture returned null', {
      level: 'error',
      tags: {
        shadowcam_diag: 'BLUE-STREAM-12010',
        shadowcam_stage: 'ensure_capture',
        peer_type: peerType,
      },
      fingerprint: ['shadowcam-capture-failed', peerType],
      extra: { requestedShadowcam: shadowcam },
    });
  }

  /** Returns true when the offer was refused (caller must abort). */
  private refuseOfferIfShadowcamUnhealthy(peerId: string, peerType: PeerType): boolean {
    // Admins/remote hosts may connect for diagnostics without ShadowCam hardware.
    // Players must not join onto a host that cannot capture instance video.
    if (peerType !== 'player') {
      return false;
    }

    const monitor = this.mediaCoordinator?.getShadowcamPresenceMonitor();
    const health = monitor?.getHealth();
    if (!health || health.healthy) {
      return false;
    }

    sentryLog.warn(
      `[PeerCoordinator] Refusing ${peerId}: shadowcams unhealthy (${health.reason ?? 'unknown'})`,
    );
    return true;
  }

  /** Provisioning snapshot breadcrumb so downstream Sentry events carry device context. */
  private logAcceptIncomingPeerBreadcrumb(
    peerId: string,
    peerType: PeerType,
    videoTrack: MediaStreamTrack | null,
    audioTrack: MediaStreamTrack | null,
  ): void {
    const deviceConfig = this.mediaCoordinator?.getDeviceConfig();
    const counts = deviceConfig?.getEnumeratedDeviceCounts() ?? {
      audioinput: 0,
      videoinput: 0,
      audiooutput: 0,
    };
    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      level: 'info',
      message: 'acceptIncomingPeer device snapshot',
      data: {
        peerId,
        peerType,
        audioinput: counts.audioinput,
        videoinput: counts.videoinput,
        audiooutput: counts.audiooutput,
        shadowcams: deviceConfig?.getShadowcams().length ?? 0,
        audioCasterPresent: this.mediaCoordinator?.hasAudioCaster() ?? false,
        hasIncomingVideoTrack: !!videoTrack,
        hasIncomingAudioTrack: !!audioTrack,
      },
    });
  }

  // ========================================
  // Internal - Track Changes
  // ========================================

  private async handleTracksChanged(videoTrack: MediaStreamTrack | null, audioTrack: MediaStreamTrack | null): Promise<void> {
    console.log('[PeerCoordinator] Tracks changed from MediaCoordinator', {
      hasVideo: !!videoTrack,
      hasAudio: !!audioTrack,
    });

    if (videoTrack && this.previousVideoTrack && this.previousVideoTrack !== videoTrack) {
      console.log('[PeerCoordinator] Video track changed, replacing on all peers');
      await this.connectionManager.replaceTrackOnAll(this.previousVideoTrack, videoTrack);
    }

    if (audioTrack && this.previousAudioTrack && this.previousAudioTrack !== audioTrack) {
      console.log('[PeerCoordinator] Audio track changed, replacing on all peers');
      await this.connectionManager.replaceTrackOnAll(this.previousAudioTrack, audioTrack);
    }

    if (videoTrack) this.previousVideoTrack = videoTrack;
    if (audioTrack) this.previousAudioTrack = audioTrack;

  }

  // ========================================
  // Internal - Data Channel
  // ========================================

  private handleRemoteStream(peerId: string, stream: MediaStream | undefined): void {
    if (!stream) {
      console.log(`[PeerCoordinator] Remote stream event from ${peerId} with no stream (expected for admin peers)`);
      return;
    }
    console.log(`[PeerCoordinator] Remote stream from ${peerId}`, {
      tracks: stream.getTracks().length,
    });
  }

  private handleDataChannelMessage(peerId: string, data: string | ArrayBuffer): void {
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data) as unknown;
        if (this.isRequestKeyframeMessage(parsed)) {
          this.connectionManager.requestKeyframeForPeer(peerId, { source: 'client' });
          return;
        }
        if (isPlayerActivityMessage(parsed)) {
          this.connectionManager.onPlayerActivity(peerId);
          return;
        }
        // The latency probe handshake + calibrated reports are consumed by the manager
        // before the stats router sees them.
        if (this.connectionManager.handleLatencyProbeMessage(peerId, data)) {
          return;
        }
        // DEBUG BUILD ONLY — a `?debug` guest forwards its stats sampler/telem as
        // `guestDebug` messages so the field trace lives in ONE console (the host's).
        if (isGuestDebugMessage(parsed)) {
          console.log('[GuestDebug.host]', {
            peerId,
            kind: parsed.kind,
            detail: parsed.detail,
          });
          // DEBUG BUILD ONLY — mirror the guest's wire copy to the on-disk NDJSON sink
          // (C:\ProgramData\Playcast\logs\webrtc-debug.ndjson) when running inside
          // host-electron, so a replay needs neither the phone nor DevTools.
          sinkWebrtcDebugLine(
            JSON.stringify({
              t: new Date().toISOString(),
              tag: '[GuestDebug.host]',
              peerId,
              kind: parsed.kind,
              detail: parsed.detail,
            }),
          );
          return;
        }
        // Guest health messages (measured clientStats, or a legacy congestion verdict from
        // an older client) are dispatched by ClientStatsRouter rather than branched here.
        if (this.connectionManager.routeClientStatsMessage(peerId, parsed)) {
          return;
        }
        this.notifyTypeHandler(peerId, parsed);
        this.events.dataReceived.emit({ peerId, data: parsed });
      } catch {
        console.warn(`[PeerCoordinator] Invalid JSON from peer ${peerId}`);
      }
    } else {
      this.events.dataReceived.emit({ peerId, data });
    }
  }

  /** Phase 1 wire contract: `{"type":"requestKeyframe"}`. */
  private isRequestKeyframeMessage(message: unknown): boolean {
    return (
      typeof message === 'object' &&
      message !== null &&
      (message as { type?: unknown }).type === 'requestKeyframe'
    );
  }

  private notifyTypeHandler(peerId: string, message: unknown): void {
    const peer = this.peerStore.currentState.peers[peerId];
    if (!peer) return;

    const raw = message as Record<string, unknown>;
    const header = raw?.header as Record<string, unknown> | undefined;
    const serializable: SerializableMessage = {
      type: (header?.target as string) ?? 'unknown',
      payload: raw,
    };
    this.typeRegistry.handleMessage(peer.peerType, peerId, serializable);
  }

  // ========================================
  // Internal - Event Emission
  // ========================================

  private readonly joinedEventMap: Record<PeerType, CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>> = {
    player: this.events.onPlayerJoined as CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>,
    admin: this.events.onAdminJoined as CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>,
    remoteHost: this.events.onRemoteHostJoined as CoordinatorEventImpl<{ peerId: string; metadata: PeerMetadata }>,
  };

  private readonly leftEventMap: Record<PeerType, CoordinatorEventImpl<{ peerId: string }>> = {
    player: this.events.onPlayerLeft as CoordinatorEventImpl<{ peerId: string }>,
    admin: this.events.onAdminLeft as CoordinatorEventImpl<{ peerId: string }>,
    remoteHost: this.events.onRemoteHostLeft as CoordinatorEventImpl<{ peerId: string }>,
  };

  private emitPeerJoinedEvent(peerType: PeerType, peerId: string, metadata: PeerMetadata): void {
    this.joinedEventMap[peerType]?.emit({ peerId, metadata });
  }

  private emitPeerLeftEvent(peerType: PeerType, peerId: string): void {
    this.leftEventMap[peerType]?.emit({ peerId });
  }

  // ========================================
  // Internal - Type Registration
  // ========================================

  private registerDefaultHandlers(): void {
    const peerTypes: PeerType[] = ['player', 'admin', 'remoteHost'];
    for (const type of peerTypes) {
      this.typeRegistry.register(type, {
        onConnected: (peerId, metadata) => console.log(`[PeerCoordinator] ${type}: connected ${peerId}`, metadata),
        onDisconnected: (peerId) => console.log(`[PeerCoordinator] ${type}: disconnected ${peerId}`),
        onMessage: (peerId, message) => console.log(`[PeerCoordinator] ${type}: message from ${peerId}`, message.type),
      });
    }
  }

  private mapPeerIdTypeToPeerType(peerIdType: string | undefined): PeerType | null {
    switch (peerIdType) {
      case 'player':
        return 'player';
      case 'admin':
        return 'admin';
      case 'remoteHost':
      case 'remoteClient':
        return 'remoteHost';
      default:
        return null;
    }
  }
}
